/**
 * Review Design Preview — mock ViewModel only.
 * No pricing / SSOT math. Sticky totals come from bookingSummary strings.
 */

import { getMockExtrasCatalog, buildMockExtrasBookingSummary } from "../extras/extrasMockData";

const EXTRAS_STATE_FOR_REVIEW = "paid";

/**
 * Build Review screen ViewModel for Design Preview.
 * Uses extras “paid” catalog selections so sticky baggage breakdown matches.
 */
export function getMockReviewViewModel(offer) {
  const catalog = getMockExtrasCatalog(EXTRAS_STATE_FOR_REVIEW);
  const selections = catalog.initialSelections || {};
  const bookingSummary = buildMockExtrasBookingSummary({
    baseSummary: offer?.bookingSummary || {},
    catalog,
    selections,
    otherSelections: {},
  });

  return {
    title: "סיכום והזמנה",
    subtitle: "בדקו את הפרטים לפני המשך לתשלום.",
    flight: {
      title: "הטיסה",
      airlineLabel: offer?.airline?.name || "Arkia",
      fareFamily: offer?.fare?.family || "CHARTER",
      legs: [
        {
          id: "outbound",
          directionLabel: "הלוך",
          timeRouteLabel: `${offer?.outbound?.depTime || "16:30"} ${offer?.outbound?.depIata || "TLV"} → ${offer?.outbound?.arrTime || "17:30"} ${offer?.outbound?.arrIata || "LCA"}`,
          metaLabel: `${offer?.outbound?.stopsLabel || "ישיר"} · ${offer?.outbound?.flightNumber || "IZ1163"} · ${offer?.outbound?.date || "08/10/2026"}`,
        },
        {
          id: "inbound",
          directionLabel: "חזור",
          timeRouteLabel: `${offer?.inbound?.depTime || "09:50"} ${offer?.inbound?.depIata || "LCA"} → ${offer?.inbound?.arrTime || "10:50"} ${offer?.inbound?.arrIata || "TLV"}`,
          metaLabel: `${offer?.inbound?.stopsLabel || "ישיר"} · ${offer?.inbound?.flightNumber || "IZ164"} · ${offer?.inbound?.date || "11/10/2026"}`,
        },
      ],
      editLabel: "עריכה",
      editTarget: "details",
    },
    passengers: {
      title: "נוסעים",
      editLabel: "עריכה",
      editTarget: "passengers",
      items: [
        {
          id: "pax-1",
          name: "David Cohen",
          metaLabel: "מבוגר · 12/04/1988 · ישראל",
          statusLabel: "✓ פרטים הושלמו",
          statusTone: "complete",
          passportSummary: "דרכון · IL · ••••7665",
        },
        {
          id: "pax-2",
          name: "Noa Cohen",
          metaLabel: "מבוגר · 03/09/1992 · ישראל",
          statusLabel: "✓ פרטים הושלמו",
          statusTone: "complete",
          passportSummary: "דרכון · IL · ••••3445",
        },
      ],
    },
    baggage: {
      title: "כבודה ותוספות",
      editLabel: "עריכה",
      editTarget: "extras",
      passengers: [
        {
          id: "pax-1",
          name: "David Cohen",
          segments: [
            {
              id: "outbound",
              routeLabel: null,
              itemLabel: "הלוך · מזוודה 20 ק״ג",
              valueLabel: "+$35",
              tone: "paid",
            },
            {
              id: "inbound",
              routeLabel: null,
              itemLabel: "חזור · מזוודה 20 ק״ג",
              valueLabel: "+$35",
              tone: "paid",
            },
          ],
          subtotalLabel: "סה״כ David Cohen",
          subtotalAmountLabel: "$70",
        },
        {
          id: "pax-2",
          name: "Noa Cohen",
          segments: [
            {
              id: "included",
              routeLabel: null,
              itemLabel: "טרולי 8 ק״ג",
              valueLabel: "כלול",
              tone: "included",
            },
            {
              id: "none",
              routeLabel: null,
              itemLabel: "לא נבחרה מזוודה נוספת",
              valueLabel: null,
              tone: "muted",
            },
          ],
          subtotalLabel: "סה״כ Noa Cohen",
          subtotalAmountLabel: "$0",
        },
      ],
      paidTotalLabel: "סה״כ כבודה בתשלום",
      paidTotalAmountLabel: "$70",
    },
    contact: {
      title: "פרטי איש קשר",
      editLabel: "עריכה",
      editTarget: "passengers",
      name: "Sara Levi",
      phone: "050-9876543",
      email: "sara.levi@example.com",
    },
    fareConditions: {
      title: "תנאי כרטיס",
      rows: [
        { label: "שינוי", value: "בתשלום" },
        { label: "ביטול", value: "לא ניתן" },
        { label: "החזר", value: "לא ניתן" },
        { label: "Ticketing", value: "24 שעות לאחר הזמנה" },
      ],
      fullTermsLabel: "צפה בתנאים המלאים",
      fullTerms:
        offer?.conditions?.fullTerms ||
        "שינוי כרטיס כפוף לזמינות ולעמלת ספק. ביטול אינו מזכה בהחזר. כרטוס נדרש תוך 24 שעות מאישור ההזמנה.",
    },
    confirmation: {
      title: "אישור לפני תשלום",
      note: "Preview בלבד — חובת האישור תיקבע לפי business rules בעת integration.",
      items: [
        {
          id: "names",
          label: "בדקתי ששמות הנוסעים תואמים למסמכי הנסיעה",
        },
        {
          id: "terms",
          label:
            "קראתי ואישרתי את תנאי הכרטיס, הכבודה והשינויים/ביטולים",
        },
      ],
    },
    cta: {
      continueLabel: "המשך לתשלום",
      backLabel: "חזרה לתוספות",
      /** Preview: require checkboxes before continue. */
      requireConfirmation: true,
    },
    bookingSummary,
  };
}
